import * as React from "react";
import styled from "@emotion/styled";
import { Navigate, useLocation } from "react-router-dom";
import {
  Button,
  Checkbox,
  FlexRow,
  FieldError,
  Form,
  H1,
  Label,
  Legend,
  Link,
  Radio,
  TagCheckbox,
  Text,
  TextArea,
  SiteContainer,
  Select,
} from "atoms";

import reactionMustHugSrc from "atoms/icons/reaction_must-hug-2.svg";
import reactionChefsKissSrc from "atoms/icons/reaction_chefs-kiss.svg";
import reactionMindBlowingSrc from "atoms/icons/reaction_mind-blowing.svg";
import reactionNeedItNowSrc from "atoms/icons/reaction_need-it-now.svg";
import reactionObsessedSrc from "atoms/icons/reaction_obsessed.svg";
import reactionOutThisWorldSrc from "atoms/icons/reaction_out-of-this-world.svg";
import reactionTakeMyMoneySrc from "atoms/icons/reaction_take-my-money.svg";
import reactionThoughtProvokingSrc from "atoms/icons/reaction_thought-provoking.svg";

import { useAuth, useAuthHasRole } from "features/auth";
import { useProfilePoints } from "features/profile";
import { Artist } from "features/reviewable/types";
import { ReviewableSummary } from "features/reviewable/components/ReviewableSummary";
import { scrollToTop } from "utils/scroll";
import { constants } from "config";
import { ArtistReview } from "../types";
import { useArtistReviewForm } from "../api/useArtistReviewForm";
import { useReview } from "../api/useReview";

import reviewStatusOptions from "../data/reviewStatusOptions.json";

type ArtistReviewProps = {
  artistId: string;
  artist?: Artist;
  review?: ArtistReview;
};

const POINTS_TO_STAKE = 10;

const ratingIcons = {
  "Must Hug": reactionMustHugSrc,
  "Chef's kiss": reactionChefsKissSrc,
  "Mind-blowing": reactionMindBlowingSrc,
  "Need it now": reactionNeedItNowSrc,
  Obsessed: reactionObsessedSrc,
  "Out of this world": reactionOutThisWorldSrc,
  "Take my money": reactionTakeMyMoneySrc,
  "Thought-provoking": reactionThoughtProvokingSrc,
};
type RatingLabel = keyof typeof ratingIcons;

const artTags = [
  "Bright and Bold",
  "Dark and Deadly",
  "Detailed and Intricate",
  "Museum-Worthy",
  "Original and Creative",
  "Retro Appeal",
  "Sparks Joy",
  "Thought-Provoking",
];

const validateTags = (value?: string[]) =>
  (value && value.length <= 2) || "You can only select up to 2 tags";

const textFieldRequirements = {
  required: "This field is required",
  maxLength: { value: 500, message: "Keep your answer under 500 characters" },
};

function ArtistReviewForm({ artistId, artist, review }: ArtistReviewProps) {
  const { user } = useAuth();
  const isReviewer = useAuthHasRole([
    "curator-level-1",
    "curator-level-2",
    "curator-level-3",
  ]);
  const isAdmin = useAuthHasRole(["admin", "superAdmin"]);

  const { data: balance } = useProfilePoints();
  const canStakePoints = !!balance && balance.pointsBalance >= POINTS_TO_STAKE;

  const location = useLocation();

  // Check database - Rejected reviews are not in useReviews
  const { data: existingReview } = useReview(
    "ARTIST",
    artistId,
    user?.id || ""
  );

  const {
    register,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    onSubmit,
    mutation: { isError: isSubmitError },
    watching: { artTagValues },
  } = useArtistReviewForm(artistId, review);

  if (!review && existingReview) {
    return (
      <Main>
        <ReviewHeader>
          <Text>
            {existingReview.status === "REJECTED"
              ? "You already have a rejected review for this artist."
              : "You have already reviewed this artist."}
          </Text>
          <Text>
            <ReturnLink
              variant="gradient"
              arrow="dr"
              to={`/artists/${artistId}`}
            >
              Return to Artist
            </ReturnLink>
          </Text>
        </ReviewHeader>
      </Main>
    );
  }

  if (!isReviewer) {
    return (
      <Navigate to="/become-a-reviewer" replace state={{ from: location }} />
    );
  }

  if (!isSubmitError && isSubmitSuccessful) {
    scrollToTop();
    const newTotalApproved =
      (balance?.lifetimeEarnings || 0) + constants.pointsEarnedReviewApproved;
    const newTotalRejected =
      (balance?.lifetimeEarnings || 0) - constants.pointsStakedReview;

    return (
      <Main>
        <ReviewHeader>
          <H1>Thank you for your&nbsp;review!</H1>

          <Text>
            You have staked {constants.pointsStakedReview} HUG Points for your
            review.
          </Text>

          <Text size="xs">
            You will receive {constants.pointsEarnedReviewApproved} HUG Points
            if your review is approved, for a lifetime total of{" "}
            {newTotalApproved}.
          </Text>

          <Text size="xs">
            If your review is rejected, you will lose your staked HUG Points and
            your lifetime total will be {newTotalRejected}.
          </Text>

          <ReturnLink variant="gradient" arrow="dr" to={`/artists/${artistId}`}>
            Return to Artist
          </ReturnLink>
        </ReviewHeader>

        <div />
      </Main>
    );
  }

  return (
    <Main>
      {review ? (
        <ReviewHeader>
          <H1>Update Review</H1>
          <SReviewableSummary
            reviewable={artist}
            showDescription={false}
            inline
          />
        </ReviewHeader>
      ) : (
        <ReviewHeader>
          <H1>Write a Review</H1>
          <SReviewableSummary
            reviewable={artist}
            showDescription={false}
            inline
          />

          {canStakePoints ? (
            <Text size="sm">
              Share your thoughts on {artist?.name} below. You will be staking
              50 HUG Points for this review.
            </Text>
          ) : (
            <>
              <SText size="md">
                Uh oh! You don&apos;t have enough HUG Points to write a review
                at the moment.
              </SText>

              <SText size="sm">
                <strong>Current Balance:</strong> {balance?.pointsBalance ?? 0}
              </SText>

              <ReturnLink
                variant="gradient"
                arrow="dr"
                to={`/artists/${artistId}`}
              >
                Return to Artist
              </ReturnLink>
            </>
          )}
        </ReviewHeader>
      )}

      {canStakePoints && (
        <SForm onSubmit={onSubmit}>
          {isAdmin && (
            <Label>
              Review Status
              <Select {...register("status", { required: true })}>
                {reviewStatusOptions.map((status) => (
                  <option key={status} value={status.toUpperCase()}>
                    {status}
                  </option>
                ))}
              </Select>
            </Label>
          )}
          <Fieldset>
            <Legend>
              How would you rate this artist?{" "}
              <RequiredLabel>(Required)</RequiredLabel>
            </Legend>
            <RatingGrid>
              {Object.keys(ratingIcons).map((key) => (
                <RatingRadio
                  {...register("rating", { required: true })}
                  label={key}
                  value={key}
                  key={key}
                />
              ))}
            </RatingGrid>

            {errors.rating && <FieldError>Please choose a rating.</FieldError>}
          </Fieldset>

          <Label>
            This artist may be for you if...{" "}
            <RequiredLabel>(Required)</RequiredLabel>
            <TextArea
              {...register("forYouIf", textFieldRequirements)}
              isInvalid={!!errors.forYouIf}
              aria-invalid={!!errors.forYouIf}
            />
            {errors.forYouIf && (
              <FieldError>{errors.forYouIf.message}</FieldError>
            )}
          </Label>

          <Fieldset>
            <Legend>
              Describe the Art
              <br />
              <small>Select up to 2</small>
            </Legend>
            {artTags.map((value) => (
              <TagCheckbox
                variant="art"
                {...register("artTags", {
                  validate: validateTags,
                })}
                label={value}
                value={value}
                key={value}
                disabled={
                  artTagValues?.length >= 2 && !artTagValues?.includes(value)
                }
              />
            ))}
            {errors.founderTeamTags && (
              <FieldError block>{errors.founderTeamTags.message}</FieldError>
            )}
          </Fieldset>

          <InlineQuestion>
            I have minted this artist&apos;s work
            <FlexRow>
              <Checkbox label="Yes" {...register("reviewerMinted")} />
            </FlexRow>
          </InlineQuestion>

          <InlineQuestion>
            I currently hold this artist&apos;s work
            <FlexRow>
              <Checkbox label="Yes" {...register("reviewerHolding")} />
            </FlexRow>
          </InlineQuestion>

          <Fieldset>
            <Legend>
              How would you like your name to be displayed on the review?
            </Legend>

            <Radio
              {...register("isAnonymous")}
              value="false"
              label={`Use my username (${
                review ? review.displayName : user?.name
              })`}
            />
            <Radio
              {...register("isAnonymous")}
              value="true"
              label="Hide my username (Anonymous user)"
            />
          </Fieldset>

          <div>
            <Button type="submit" variant="gradient" disabled={isSubmitting}>
              Submit Review
            </Button>

            {isSubmitError && (
              <FieldError block>
                Sorry, something went wrong. Try again.
              </FieldError>
            )}

            <SText size="xs">
              <em>
                By submitting this review, I confirm this review is about my own
                personal experience, and I agree to stake 50 of my HUG Points.
              </em>
            </SText>
          </div>
        </SForm>
      )}
    </Main>
  );
}

const Main = styled(SiteContainer)({
  minHeight: "80vh",
}).withComponent("main");

const Fieldset = styled(Label)({}).withComponent("fieldset");

const ReviewHeader = styled.div({
  padding: "0 0 2rem ",
});

const SForm = styled(Form)({
  paddingTop: "2rem",
  gap: "4rem",
});

const InlineQuestion = styled(Label)(({ theme }) => ({
  ...theme.fonts.body,
  alignItems: "center",
  display: "inline-flex",
  fontSize: theme.fontSizes.sm,
  fontWeight: "700",
  gap: "1.5rem",
  maxWidth: "max-content",
})).withComponent("div");

const SText = styled(Text)({
  margin: "1em 0",
});
const RatingGrid = styled.div({
  display: "grid",
  placeItems: "center",
  gap: "1.5em 2px",
  gridTemplateColumns: "repeat(auto-fit, minmax(92px, 1fr))",
  "@media (min-width: 480px)": {
    placeItems: "start",
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  "@media (min-width: 831px)": {
    gridTemplateColumns: "repeat(8, 1fr)",
  },
});
const RatingRadio = styled(Radio)(({ label, theme }) => ({
  position: "relative",
  flexDirection: "column",
  alignItems: "center",
  flex: "1 1 70px",
  gap: "5px",
  marginBottom: 0,
  maxWidth: "90px",
  textAlign: "center",
  justifyContent: "flex-start",
  input: {
    border: 0,
    height: 60,
    width: 60,
    transition: "all 0.15s ease",
    backgroundColor: "transparent",
  },
  "input:hover": {
    backgroundColor: theme.colors.purple,
  },
  "input::after": {
    content: "none",
  },
  "input::before": {
    all: "unset",
    backgroundImage: `url("${ratingIcons[label as RatingLabel]}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    content: "''",
    clipPath: "none",
    width: 50,
    height: 50,
    pointerEvents: "none",
    transform: "translate(2%, 2%)",
    transition: "all 0.15s ease",
  },
  "input:checked": {
    backgroundColor: theme.colors.primary,
  },
  div: {
    ...theme.fonts.body,
    border: "1px solid rgba(0,0,0,0)",
    borderRadius: theme.borderRadius.round,
    fontSize: "14px",
    padding: "2px 8px 1px",
    transition: "all 0.15s ease",
    lineHeight: 1,
  },
  "input:checked + div": {
    borderColor: "black",
  },
  "@media (min-width: 400px)": {
    input: {
      height: 70,
      width: 70,
    },
    "input::before": {
      width: 60,
      height: 60,
    },
  },
}));
const RequiredLabel = styled.small(({ theme }) => ({
  color: theme.colors.error,
  fontStyle: "italic",
}));
const ReturnLink = styled(Link)({
  margin: "2rem 0",
});
const SReviewableSummary = styled(ReviewableSummary)(({ theme }) => ({
  img: {
    borderRadius: theme.borderRadius.round,
  },
}));
export { ArtistReviewForm };
